import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, MessageCircle, MapPin, Loader2, Calendar, ExternalLink, Car
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { User, Trip, ItineraryDay } from '../types/database';
import FlightSearch from '../components/FlightSearch';
import HotelSearch from '../components/HotelSearch';
import ActivitiesSearch from '../components/ActivitiesSearch';
import WeatherWidget from '../components/WeatherWidget';
import GroupTripManager from '../components/GroupTripManager';

interface TripDetailsProps {
  user: User;
}

export default function TripDetails({ user: _user }: TripDetailsProps) {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [days, setDays] = useState<ItineraryDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'itinerary' | 'chat' | 'flights' | 'hotels' | 'activities'>('itinerary');

  useEffect(() => {
    if (tripId) loadTripData();
  }, [tripId]);

  const loadTripData = async () => {
    try {
      // Load trip
      const { data: tripData, error: tripError } = await supabase
        .from('trips')
        .select('*')
        .eq('id', tripId)
        .single();
      
      if (tripError) throw tripError;
      setTrip(tripData);

      // Load days
      const { data: daysData, error: daysError } = await supabase
        .from('itinerary_days')
        .select('*')
        .eq('trip_id', tripId)
        .order('day_number', { ascending: true });
      
      if (daysError) throw daysError;
      
      // If no days exist, create them based on trip dates
      if (!daysData || daysData.length === 0) {
        await createInitialDays(tripData);
      } else {
        setDays(daysData);
      }
    } catch (err) {
      console.error('Error loading trip:', err);
    } finally {
      setLoading(false);
    }
  };

  const createInitialDays = async (tripData: Trip) => {
    if (!tripData.start_date || !tripData.end_date) return;
    
    const start = new Date(tripData.start_date);
    const end = new Date(tripData.end_date);
    const numDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    const newDays = [];
    for (let i = 0; i < numDays; i++) {
      const dayDate = new Date(start);
      dayDate.setDate(dayDate.getDate() + i);
      newDays.push({
        trip_id: tripData.id,
        day_number: i + 1,
        date: dayDate.toISOString().split('T')[0],
        title: `Day ${i + 1}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }

    const { data, error } = await supabase
      .from('itinerary_days')
      .insert(newDays)
      .select();
    
    if (!error && data) {
      setDays(data);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Trip not found</h2>
          <Link to="/dashboard" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{trip.title}</h1>
                <p className="text-sm text-gray-600 flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {trip.destination}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Link
                to={`/trip/${tripId}/road-trip`}
                className="btn-secondary flex items-center space-x-2"
                title="Plan a road trip with multiple stops"
              >
                <Car className="h-4 w-4" />
                <span>Road Trip</span>
              </Link>
              <Link
                to={`/trip/${tripId}/itinerary`}
                className="btn-secondary flex items-center space-x-2"
                title="Open full itinerary builder with map"
              >
                <Calendar className="h-4 w-4" />
                <span>Full Itinerary</span>
              </Link>
              <Link to={`/trip/${tripId}/chat`} className="btn-primary flex items-center space-x-2">
                <MessageCircle className="h-4 w-4" />
                <span>Chat with AI</span>
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 mt-4 -mb-4">
            {(['itinerary', 'chat', 'flights', 'hotels', 'activities'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeTab === tab
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'itinerary' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Itinerary */}
            <div className="lg:col-span-2 space-y-6">
              {days.map((day: ItineraryDay) => (
                <div key={day.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{day.title}</h3>
                        {day.date && (
                          <p className="text-primary-100 text-sm">
                            {new Date(day.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        )}
                      </div>
                      <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                        Day {day.day_number}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    {/* Day activities would go here */}
                    <p className="text-gray-500 text-center py-8">
                      No activities planned yet. Start chatting with AI to build your itinerary!
                    </p>
                    <div className="mt-4 flex justify-center space-x-4">
                      <Link
                        to={`/trip/${tripId}/chat`}
                        className="btn-secondary flex items-center space-x-2"
                      >
                        <MessageCircle className="h-4 w-4" />
                        <span>Plan with AI</span>
                      </Link>
                      <Link
                        to={`/trip/${tripId}/itinerary`}
                        className="btn-primary flex items-center space-x-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        <span>Open Full Itinerary</span>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Weather Widget */}
              <WeatherWidget
                location={trip.destination}
                showForecast={true}
                compact={false}
              />

              {/* Group Trip Manager (demo mode) */}
              <GroupTripManager
                tripId={tripId || 'demo'}
                userId={_user.id}
              />
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center py-20">
            <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Chat with Your AI Travel Planner</h3>
            <p className="text-gray-600 mb-6">
              Describe your ideal trip and let AI create a personalized itinerary for you.
            </p>
            <Link to={`/trip/${tripId}/chat`} className="btn-primary">
              Open Chat
            </Link>
          </div>
        )}

        {activeTab === 'flights' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <FlightSearch />
          </div>
        )}

        {activeTab === 'hotels' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <HotelSearch initialCity={trip.destination?.split(',')[0]?.trim().toUpperCase().slice(0, 3) || ''} />
          </div>
        )}

        {activeTab === 'activities' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <ActivitiesSearch />
          </div>
        )}
      </main>
    </div>
  );
}
